// Ticket csatolmány-kezelés (belső nézet) — a detail-oldal külön szekciója. A MEGJELENÍTÉS és a viselkedés a
// KÖZÖS `components/shared/attachment-panel.tsx`-ben él (ugyanezt használja az üzenet-melléklet); itt csak a
// ticket-specifikus rész marad: a lista-query, a KÉT végpont-hívás, a cache-invalidálás és a SZÖVEGEK.
//
// A feltöltés a 3-lépéses presigned-flow (`lib/attachment-rules.ts`): upload-url → PUT közvetlenül a tárba →
// commit. A kliens-oldali validáció csak UX — a BFF a végső, megkerülhetetlen fal.
import { type TicketAttachmentRowSchema } from '@atlas/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import {
  AttachmentPanel,
  type AttachmentDeleteOutcome,
} from '@/components/shared/attachment-panel';
import { ticketKeys } from '@/features/tickets/api';
import { MAX_ATTACHMENTS, uploadTicketAttachment } from '@/features/tickets/attachment-upload';
import { TICKET_SECTION } from '@/features/tickets/sections';
import { api } from '@/lib/api';

type TicketAttachment = z.infer<typeof TicketAttachmentRowSchema>;

export interface TicketAttachmentsProps {
  ticketId: string;
  /** Feltöltés/törlés joga (tickets.manage) — enélkül csak olvasható a lista. */
  canManage: boolean;
}

export function TicketAttachments({ ticketId, canManage }: TicketAttachmentsProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const queryClient = useQueryClient();

  const listKey = ticketKeys.attachments(ticketId);
  const listQuery = api.ticketAttachments.list.useQuery(listKey, { params: { id: ticketId } });
  const attachments: TicketAttachment[] = listQuery.data?.status === 200 ? listQuery.data.body : [];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: listKey });
    void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
  };

  const onDelete = async (attachmentId: string): Promise<AttachmentDeleteOutcome> => {
    const res = await api.ticketAttachments.delete.mutation({
      params: { id: ticketId, attachmentId },
    });
    if (res.status === 200) return 'ok';
    if (res.status === 403) return 'forbidden';
    return 'failed';
  };

  return (
    <AttachmentPanel
      // A jegy-detail FORM-oldal (nincsenek kártya-blokkok) → szekció-fejléc + keret nélküli konténer.
      headerVariant="section"
      className="scroll-mt-6 space-y-4"
      id={TICKET_SECTION.attachments}
      rows={attachments}
      loading={listQuery.isLoading}
      canManage={canManage}
      onUpload={(file) => uploadTicketAttachment(ticketId, file)}
      onDelete={onDelete}
      onChanged={invalidate}
      // a sorban kapott URL percek alatt lejár → kattintáskor friss (Évi #79)
      freshDownload={{
        resolve: async (attachmentId) => {
          const res = await api.ticketAttachments.downloadUrl.query({
            params: { id: ticketId, attachmentId },
          });
          return res.status === 200 ? res.body.url : null;
        },
        failed: t('toast:tickets.attachmentDownloadFailed'),
      }}
      formatDate={(iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(locale);
      }}
      labels={{
        title: t('tickets.attachments.title'),
        upload: t('tickets.attachments.upload'),
        uploading: t('tickets.attachments.uploading'),
        empty: t('tickets.attachments.empty'),
        download: t('tickets.attachments.download'),
        remove: t('tickets.attachments.delete'),
        removing: t('tickets.attachments.deleting'),
        removeTitle: t('tickets.attachments.deleteTitle'),
        removeDescription: (name) => t('tickets.attachments.deleteDesc', { name }),
        cancel: t('tickets.cancel'),
        unavailable: t(`tickets.attachments.unavailable`),
        limitReached: t('tickets.attachments.limitReached', { max: MAX_ATTACHMENTS }),
      }}
      messages={{
        limit: t('toast:tickets.attachmentLimit', { max: MAX_ATTACHMENTS }),
        invalidType: t('toast:tickets.attachmentInvalidType'),
        tooLarge: t('toast:tickets.attachmentTooLarge'),
        uploaded: t('toast:tickets.attachmentUploaded'),
        uploadFailed: t('toast:tickets.attachmentError'),
        forbidden: t('toast:tickets.forbidden'),
        validation: t('toast:tickets.attachmentValidation'),
        // A ticket-csatolmány tenantja a ticketből jön → „nincs konfigurálva" eset itt nem fordul elő;
        // a közös panel viszont kér üzenetet, ezért a generikus hibát adjuk.
        unavailable: t('toast:tickets.attachmentError'),
        deleted: t('toast:tickets.attachmentDeleted'),
      }}
    />
  );
}
